/**
 * Einwegpfand-Sync (mobil-sicher: iOS/Android/Desktop)
 * - Mengen serialisiert + atomar
 * - Checkout erst bei Pfand === Pflichtmenge
 * - Fetch mit Timeout/Retry, Cache-Bust, BFCache-Resync
 */
(() => {
  'use strict';

  const DEFAULT_VARIANT_ID = 53102924661000;
  const DEFAULT_SECTIONS =
    'cart-drawer,main-cart-items,main-cart-footer,cart-icon-bubble';
  const CHECKOUT_SEL =
    '[name="checkout"], .cart__checkout-button, #checkout, #CartDrawer-Checkout';
  const EXPRESS_SEL =
    '.additional-checkout-buttons, .cart__dynamic-checkout-buttons, shopify-accelerated-checkout-cart, .dynamic-checkout__content';
  const FETCH_TIMEOUT_MS = 15000;
  const FETCH_RETRIES = 2;
  const PATCH_MAX_TRIES = 80;

  let syncing = false;
  let pending = false;
  let debounceTimer = null;
  let stuckTimer = null;
  let patched = false;
  let patchTries = 0;
  let updateChain = Promise.resolve();
  let checkoutBusy = false;
  let matched = false;
  let checkoutGateInstalled = false;
  let qtyOpsInFlight = 0;
  /** @type {Record<string, { seq: number, line: *, key: string|null, quantity: *, event: *, name: *, variantId: *, self: * }>} */
  const latestIntentByKey = Object.create(null);
  let intentSeq = 0;

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function bust(url) {
    const sep = url.indexOf('?') >= 0 ? '&' : '?';
    return url + sep + '_kc=' + Date.now();
  }

  function rootPath() {
    try {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    } catch (_) {
      return '/';
    }
  }

  function cartJsUrl() {
    return bust(rootPath().replace(/\/?$/, '/') + 'cart.js');
  }

  function cartUpdateUrl() {
    try {
      if (window.routes && window.routes.cart_update_url) return window.routes.cart_update_url;
    } catch (_) {
      /* ignore */
    }
    return rootPath().replace(/\/?$/, '/') + 'cart/update.js';
  }

  function cartAddUrl() {
    return rootPath().replace(/\/?$/, '/') + 'cart/add.js';
  }

  async function fetchWithTimeout(url, options, timeoutMs) {
    const opts = options || {};
    let timer;
    const controller =
      typeof AbortController !== 'undefined' ? new AbortController() : null;
    if (controller) {
      opts.signal = controller.signal;
      timer = setTimeout(function () {
        try {
          controller.abort();
        } catch (_) {
          /* ignore */
        }
      }, timeoutMs || FETCH_TIMEOUT_MS);
    }
    try {
      const res = await fetch(url, opts);
      return res;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  async function fetchJson(url, options, retries) {
    let lastErr;
    const max = typeof retries === 'number' ? retries : FETCH_RETRIES;
    for (let i = 0; i <= max; i++) {
      try {
        const res = await fetchWithTimeout(
          url,
          Object.assign(
            {
              credentials: 'same-origin',
              cache: 'no-store',
            },
            options || {}
          ),
          FETCH_TIMEOUT_MS
        );
        const text = await res.text();
        let json;
        try {
          json = text ? JSON.parse(text) : {};
        } catch (_) {
          throw new Error(url + ' invalid json');
        }
        if (!res.ok) throw new Error(url + ' HTTP ' + res.status);
        return json;
      } catch (err) {
        lastErr = err;
        if (i < max) await sleep(250 * (i + 1));
      }
    }
    throw lastErr || new Error('fetch failed');
  }

  function isFeeItem(item) {
    if (!item) return false;
    const handle = item.handle || '';
    if (handle === 'einwegpfand' || handle === 'gebuhren') return true;
    const title = String(item.product_title || item.title || '').toLowerCase();
    return title === 'einwegpfand' || title === 'gebühren' || title === 'gebuhren';
  }

  function fetchCart() {
    return fetchJson(cartJsUrl(), { method: 'GET' });
  }

  function post(url, body) {
    return fetchJson(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
  }

  function feeVariantId() {
    const el = document.querySelector('.kc-pfand-sync');
    const raw = (el && el.getAttribute('data-variant-id')) || String(DEFAULT_VARIANT_ID);
    const n = parseInt(raw, 10);
    return isFinite(n) ? n : DEFAULT_VARIANT_ID;
  }

  function syncMeta() {
    return document.querySelector('.kc-pfand-sync');
  }

  function waitLabel() {
    try {
      if (window.cartStrings && window.cartStrings.pfandCheckoutWait) {
        return window.cartStrings.pfandCheckoutWait;
      }
    } catch (_) {
      /* ignore */
    }
    return 'Pfand wird berechnet… Bitte warten.';
  }

  function isFeeInCart(item) {
    if (!item) return false;
    return String(item.variant_id) === String(feeVariantId()) || isFeeItem(item);
  }

  /**
   * Line-Key der angeklickten Zeile (vor async!), nie Index — Pfand-Fee
   * verschiebt sonst die Line-Nummern und trifft ein anderes Produkt.
   */
  function resolveLineKey(line) {
    if (line == null || line === '') return null;
    const byId =
      document.getElementById('CartItem-' + line) ||
      document.getElementById('CartDrawer-Item-' + line);
    if (byId) {
      const k = byId.getAttribute('data-key');
      if (k) return k;
    }
    const input =
      document.getElementById('Quantity-' + line) ||
      document.getElementById('Drawer-quantity-' + line) ||
      document.querySelector('.quantity__input[data-index="' + line + '"]');
    if (input) {
      const row = input.closest('tr.cart-item');
      if (row) {
        const k = row.getAttribute('data-key');
        if (k) return k;
      }
    }
    return null;
  }

  function countNonFeeQty(cart, changedKey, newQty, changedLine) {
    if (!cart || !cart.items) return 0;
    let n = 0;
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      if (isFeeInCart(item)) continue;
      const lineNum = i + 1;
      if (changedKey && item.key === changedKey) {
        const q = parseInt(newQty, 10);
        if (isFinite(q) && q > 0) n += q;
      } else if (
        !changedKey &&
        changedLine != null &&
        lineNum === parseInt(changedLine, 10)
      ) {
        const q = parseInt(newQty, 10);
        if (isFinite(q) && q > 0) n += q;
      } else if (item.quantity > 0) {
        n += item.quantity;
      }
    }
    return n;
  }

  function sumFeeQty(cart) {
    if (!cart || !cart.items) return 0;
    let feeQty = 0;
    for (let i = 0; i < cart.items.length; i++) {
      if (isFeeInCart(cart.items[i])) feeQty += cart.items[i].quantity;
    }
    return feeQty;
  }

  /**
   * Pflicht-Pfandmenge. Nie veraltetes data-required nutzen, wenn keine
   * pfandpflichtigen Produkte mehr im Cart/DOM sind (sonst bleibt Geister-Pfand).
   */
  function computeRequired(changedKey, newQty, cart, changedLine) {
    let required = 0;
    const rows = document.querySelectorAll('tr.cart-item[data-kc-pfand="1"]');
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const input = row.querySelector('.quantity__input');
      const idx = input && input.getAttribute('data-index');
      const rowKey = row.getAttribute('data-key');
      let q = parseInt((input && input.value) || '0', 10);
      if (changedKey && rowKey && rowKey === changedKey) {
        q = parseInt(newQty, 10);
      } else if (
        !changedKey &&
        changedLine != null &&
        String(idx) === String(changedLine)
      ) {
        q = parseInt(newQty, 10);
      }
      if (isFinite(q) && q > 0) required += q;
    }

    if (cart) {
      const nonFee = countNonFeeQty(cart, changedKey, newQty, changedLine);
      // Nur noch (versteckte) Fees → Pfand muss 0 sein
      if (nonFee === 0) return 0;

      // Keys aus DOM gegen Cart abgleichen
      if (required === 0 && rows.length > 0) {
        const keySet = {};
        for (let r = 0; r < rows.length; r++) {
          const k = rows[r].getAttribute('data-key');
          if (k) keySet[k] = true;
        }
        for (let j = 0; j < cart.items.length; j++) {
          const item = cart.items[j];
          if (isFeeInCart(item) || !keySet[item.key]) continue;
          if (changedKey && item.key === changedKey) {
            const q = parseInt(newQty, 10);
            if (isFinite(q) && q > 0) required += q;
          } else if (
            !changedKey &&
            changedLine != null &&
            j + 1 === parseInt(changedLine, 10)
          ) {
            const q = parseInt(newQty, 10);
            if (isFinite(q) && q > 0) required += q;
          } else if (item.quantity > 0) {
            required += item.quantity;
          }
        }
      }

      // Produkte da, aber DOM noch ohne Flags → frisches Meta ok
      if (required === 0 && nonFee > 0) {
        const meta = syncMeta();
        const fromMeta = parseInt((meta && meta.getAttribute('data-required')) || '0', 10);
        if (isFinite(fromMeta) && fromMeta > 0) required = fromMeta;
      }
      return required;
    }

    // Ohne Cart: nur DOM, kein stale Meta-Fallback
    return required;
  }

  async function clearCartCompletely() {
    const url = bust(rootPath().replace(/\/?$/, '/') + 'cart/clear.js');
    await fetchJson(url, { method: 'POST' });
  }

  function publishCart(cartData) {
    try {
      if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'kc-pfand',
          cartData: cartData || {},
        });
      }
    } catch (_) {
      /* ignore */
    }
  }

  function clearCartErrors() {
    const texts = document.querySelectorAll('.cart-item__error-text');
    for (let i = 0; i < texts.length; i++) texts[i].textContent = '';
    const box =
      document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (box) box.textContent = '';
  }

  function setStatus(msg) {
    const hosts = document.querySelectorAll('.cart__ctas');
    for (let i = 0; i < hosts.length; i++) {
      const host = hosts[i];
      let el = host.querySelector('.kc-pfand-status');
      if (!el) {
        el = document.createElement('p');
        el.className = 'kc-pfand-status';
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        host.appendChild(el);
      } else if (el.nextSibling) {
        host.appendChild(el);
      }
      el.textContent = msg || '';
      el.hidden = !msg;
    }
  }

  function canRevealCheckout() {
    return qtyOpsInFlight === 0;
  }

  function setCheckoutReady(ready, reason) {
    if (ready && !canRevealCheckout()) {
      ready = false;
      reason = reason || waitLabel();
    }

    matched = !!ready;
    try {
      document.documentElement.classList.toggle('kc-pfand-busy', !ready);
      document.documentElement.classList.toggle('kc-pfand-ready', ready);
    } catch (_) {
      /* ignore */
    }

    const buttons = document.querySelectorAll(CHECKOUT_SEL);
    for (let i = 0; i < buttons.length; i++) {
      const btn = buttons[i];
      if (!btn || btn.nodeType !== 1) continue;
      btn.hidden = !ready;
      btn.setAttribute('aria-busy', ready ? 'false' : 'true');
      btn.setAttribute('aria-disabled', ready ? 'false' : 'true');
      if (ready) btn.removeAttribute('tabindex');
      else btn.setAttribute('tabindex', '-1');
    }

    const express = document.querySelectorAll(EXPRESS_SEL);
    for (let j = 0; j < express.length; j++) {
      const node = express[j];
      if (!node || node.nodeType !== 1) continue;
      node.hidden = !ready;
      node.setAttribute('aria-hidden', ready ? 'false' : 'true');
    }

    setStatus(ready ? '' : reason || waitLabel());
    if (!ready) armStuckWatchdog();
    else clearTimeout(stuckTimer);
  }

  function reapplyCheckoutVisibility() {
    setCheckoutReady(matched, matched ? '' : waitLabel());
  }

  function armStuckWatchdog() {
    clearTimeout(stuckTimer);
    stuckTimer = setTimeout(function () {
      if (matched || qtyOpsInFlight > 0 || checkoutBusy) return;
      scheduleSync(50);
    }, 8000);
  }

  async function refreshSections(sectionList) {
    const root = rootPath();
    const raw = sectionList || DEFAULT_SECTIONS;
    const ids = String(raw)
      .split(',')
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
    if (!ids.length) return;

    const sectionsUrl =
      root.replace(/\/?$/, '/') +
      '?sections=' +
      ids.map(encodeURIComponent).join(',') +
      '&_kc=' +
      Date.now();

    const sections = await fetchJson(sectionsUrl, { method: 'GET' });
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      const html = sections && sections[id];
      if (!html) continue;
      try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // Header-Bubble: <a id="cart-icon-bubble"> (kein shopify-section-*)
        if (id === 'cart-icon-bubble') {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) {
            bubble.innerHTML = doc.body ? doc.body.innerHTML : html;
          }
          continue;
        }
        const incoming =
          doc.getElementById('shopify-section-' + id) || doc.body.firstElementChild;
        const current = document.getElementById('shopify-section-' + id);
        if (incoming && current) {
          current.innerHTML = incoming.innerHTML;
        }
      } catch (_) {
        /* ignore DOM errors on weird mobile WebViews */
      }
    }
    clearCartErrors();
    reapplyCheckoutVisibility();
  }

  async function buildAtomicUpdates(changedKey, newQty, changedLine) {
    const cart = await fetchCart();
    const variantId = feeVariantId();
    const updates = {};
    let feeKey = null;
    let touchedProduct = false;
    const qty = parseInt(newQty, 10);

    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      if (isFeeInCart(item)) {
        if (!feeKey) feeKey = item.key;
        else updates[item.key] = 0;
        continue;
      }
      // Nur die angeklickte Line per Key (nie Index — Fee verschiebt Positionen)
      if (changedKey && item.key === changedKey) {
        updates[item.key] = isFinite(qty) ? qty : item.quantity;
        touchedProduct = true;
      } else if (
        !changedKey &&
        changedLine != null &&
        i + 1 === parseInt(changedLine, 10)
      ) {
        updates[item.key] = isFinite(qty) ? qty : item.quantity;
        touchedProduct = true;
      }
    }

    const required = computeRequired(changedKey, newQty, cart, changedLine);
    if (feeKey) updates[feeKey] = required;
    // Keine Produkte mehr → alle Fees auf 0
    if (required === 0 && feeKey) updates[feeKey] = 0;

    return {
      updates: updates,
      required: required,
      feeKey: feeKey,
      variantId: variantId,
      cart: cart,
      touchedProduct: touchedProduct,
      clearAll:
        required === 0 &&
        countNonFeeQty(cart, changedKey, newQty, changedLine) === 0 &&
        sumFeeQty(cart) > 0,
    };
  }

  async function applyRequiredFee(required) {
    const cart = await fetchCart();
    const variantId = feeVariantId();
    const nonFee = countNonFeeQty(cart, null, null);
    const feeQtyTotal = sumFeeQty(cart);

    // Nur noch Einwegpfand / Gebühren sichtbar-versteckt → Warenkorb leeren
    if (nonFee === 0 && feeQtyTotal > 0) {
      await clearCartCompletely();
      return true;
    }

    if (required === 0 && feeQtyTotal > 0 && nonFee === 0) {
      await clearCartCompletely();
      return true;
    }

    const updates = {};
    let feeKey = null;
    let feeQty = 0;

    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      if (!isFeeInCart(item)) continue;
      if (!feeKey) {
        feeKey = item.key;
        feeQty = item.quantity;
      } else {
        updates[item.key] = 0;
      }
    }

    if (feeKey) {
      if (feeQty !== required || Object.keys(updates).length) {
        updates[feeKey] = required;
        await post(cartUpdateUrl(), { updates: updates });
        return true;
      }
      return false;
    }

    if (required > 0) {
      await post(cartAddUrl(), { items: [{ id: variantId, quantity: required }] });
      return true;
    }
    return false;
  }

  async function ensurePfandMatched(maxTries) {
    const tries = maxTries || 4;
    setCheckoutReady(false, waitLabel());

    for (let i = 0; i < tries; i++) {
      if (qtyOpsInFlight > 0) return false;

      try {
        const cart = await fetchCart();
        let required = computeRequired(null, null, cart);
        const meta = syncMeta();
        const sections =
          (meta && meta.getAttribute('data-sections')) || DEFAULT_SECTIONS;

        // Geister-Pfand: keine Produkte, aber noch Fees
        if (countNonFeeQty(cart, null, null) === 0 && sumFeeQty(cart) > 0) {
          required = 0;
        }

        const changed = await applyRequiredFee(required);
        if (changed || i === 0) {
          await refreshSections(sections);
        }

        const after = await fetchCart();
        const feeQty = sumFeeQty(after);
        const nonFee = countNonFeeQty(after, null, null);
        const requiredAfter = computeRequired(null, null, after);

        // Leerer Warenkorb (auch nach Clear)
        if (nonFee === 0 && feeQty === 0) {
          if (!canRevealCheckout()) return false;
          matched = true;
          try {
            document.documentElement.classList.add('kc-pfand-ready');
            document.documentElement.classList.remove('kc-pfand-busy');
          } catch (_) {
            /* ignore */
          }
          setStatus('');
          clearCartErrors();
          publishCart(after);
          return true;
        }

        if (feeQty === requiredAfter && requiredAfter === required) {
          if (!canRevealCheckout()) return false;
          setCheckoutReady(true);
          clearCartErrors();
          publishCart(after);
          return true;
        }

        await applyRequiredFee(requiredAfter);
        await refreshSections(sections);
      } catch (err) {
        console.warn('[kc-pfand] ensure try', i, err);
        await sleep(300 * (i + 1));
      }
    }

    try {
      const finalCart = await fetchCart();
      const feeQty = sumFeeQty(finalCart);
      const requiredFinal = computeRequired(null, null, finalCart);
      if (countNonFeeQty(finalCart, null, null) === 0 && feeQty > 0) {
        await clearCartCompletely();
        await refreshSections(DEFAULT_SECTIONS);
        matched = true;
        document.documentElement.classList.add('kc-pfand-ready');
        document.documentElement.classList.remove('kc-pfand-busy');
        setStatus('');
        publishCart(await fetchCart());
        return true;
      }
      const ok = feeQty === requiredFinal && canRevealCheckout();
      setCheckoutReady(ok, ok ? '' : waitLabel());
      if (ok) publishCart(finalCart);
      return ok;
    } catch (err) {
      console.warn('[kc-pfand] ensure final', err);
      setCheckoutReady(false, waitLabel());
      armStuckWatchdog();
      return false;
    }
  }

  function isClearEvent(event) {
    try {
      if (!event || !event.currentTarget) return false;
      if (typeof CartRemoveButton !== 'undefined' && event.currentTarget instanceof CartRemoveButton) {
        return true;
      }
      if (event.currentTarget.tagName === 'CART-REMOVE-BUTTON') return true;
    } catch (_) {
      /* ignore */
    }
    return false;
  }

  function patchCartItems() {
    if (patched) return;
    const Ctor = customElements.get('cart-items');
    if (!Ctor || !Ctor.prototype || !Ctor.prototype.updateQuantity) {
      patchTries += 1;
      if (patchTries > PATCH_MAX_TRIES) {
        console.warn('[kc-pfand] cart-items not found, sync-only mode');
        return;
      }
      setTimeout(patchCartItems, 100);
      return;
    }
    patched = true;
    const proto = Ctor.prototype;
    const original = proto.updateQuantity;

    proto.updateQuantity = function (line, quantity, event, name, variantId) {
      const self = this;
      // Key sofort sichern — nach Section-Refresh sind Line-Indizes ungültig
      const changedKey = resolveLineKey(line);
      const intentKey = changedKey || '__line_' + String(line);
      const seq = ++intentSeq;
      latestIntentByKey[intentKey] = {
        seq: seq,
        line: line,
        key: changedKey,
        quantity: quantity,
        event: event,
        name: name,
        variantId: variantId,
        self: self,
      };

      const eventTarget = isClearEvent(event) ? 'clear' : 'change';
      let marker = null;
      try {
        if (window.CartPerformance && window.CartPerformance.createStartingMarker) {
          marker = window.CartPerformance.createStartingMarker(eventTarget + ':user-action');
        }
      } catch (_) {
        /* ignore */
      }

      try {
        self.enableLoading(line);
      } catch (_) {
        /* ignore */
      }
      qtyOpsInFlight += 1;
      setCheckoutReady(false, waitLabel());

      const run = function () {
        return (async function () {
          const intent = latestIntentByKey[intentKey];
          // Schnelle Mehrfachklicks: nur die neueste Menge je Produkt ausführen
          if (!intent || intent.seq !== seq) {
            qtyOpsInFlight = Math.max(0, qtyOpsInFlight - 1);
            try {
              self.disableLoading(line);
            } catch (_) {
              /* ignore */
            }
            if (qtyOpsInFlight === 0) {
              try {
                await ensurePfandMatched(4);
              } catch (e) {
                console.warn('[kc-pfand] post-ensure', e);
                setCheckoutReady(false, waitLabel());
              }
            }
            return;
          }

          const liveLine = intent.line;
          const liveKey = intent.key;
          const liveQty = intent.quantity;
          const liveName = intent.name;
          const liveVariantId = intent.variantId;
          const liveSelf = intent.self || self;

          try {
            const built = await buildAtomicUpdates(liveKey, liveQty, liveLine);
            let updates = built.updates;
            const required = built.required;
            let feeKey = built.feeKey;
            const feeVariant = built.variantId;

            // Letztes Produkt weg → Pfand mit leeren
            if (built.clearAll) {
              await clearCartCompletely();
              const emptyCart = await fetchCart();
              await refreshSections(DEFAULT_SECTIONS);
              publishCart(emptyCart);
              try {
                paintParsedState(liveSelf, emptyCart, liveLine, 0, liveName, eventTarget);
              } catch (_) {
                /* ignore */
              }
              return emptyCart;
            }

            if (!feeKey && required > 0) {
              await post(cartAddUrl(), {
                items: [{ id: feeVariant, quantity: required }],
              });
              // Nach Fee-Add erneut per Key (nicht Line-Index)
              const rebuilt = await buildAtomicUpdates(liveKey, liveQty, liveLine);
              updates = rebuilt.updates;
            }

            const sectionDefs =
              (liveSelf.getSectionsToRender && liveSelf.getSectionsToRender()) || [];
            const sectionIds = sectionDefs.map(function (s) {
              return s.section;
            });
            if (sectionIds.indexOf('cart-icon-bubble') < 0) {
              sectionIds.push('cart-icon-bubble');
            }
            const body = {
              updates: updates,
              sections: sectionIds,
              sections_url: window.location.pathname,
            };

            const state = await post(cartUpdateUrl(), body);

            if (state.errors) {
              const quantityElement =
                document.getElementById('Quantity-' + liveLine) ||
                document.getElementById('Drawer-quantity-' + liveLine);
              if (quantityElement) {
                quantityElement.value = quantityElement.getAttribute('value');
              }
              if (liveSelf.updateLiveRegions) liveSelf.updateLiveRegions(liveLine, state.errors);
              return state;
            }

            try {
              if (typeof CartPerformance !== 'undefined' && CartPerformance.measure) {
                CartPerformance.measure(eventTarget + ':paint-updated-sections', function () {
                  paintParsedState(liveSelf, state, liveLine, liveQty, liveName, eventTarget);
                });
              } else {
                paintParsedState(liveSelf, state, liveLine, liveQty, liveName, eventTarget);
              }
            } catch (paintErr) {
              console.warn('[kc-pfand] paint', paintErr);
              paintParsedState(liveSelf, state, liveLine, liveQty, liveName, eventTarget);
            }

            clearCartErrors();
            try {
              if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
                window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                  source: 'kc-pfand',
                  cartData: state,
                  variantId: liveVariantId,
                });
              }
            } catch (_) {
              /* ignore */
            }
            return state;
          } catch (err) {
            console.warn('[kc-pfand] atomic update failed, fallback', err);
            try {
              await original.call(liveSelf, liveLine, liveQty, event, liveName, liveVariantId);
            } catch (fallbackErr) {
              console.warn('[kc-pfand] fallback failed', fallbackErr);
            }
          } finally {
            if (latestIntentByKey[intentKey] && latestIntentByKey[intentKey].seq === seq) {
              delete latestIntentByKey[intentKey];
            }
            qtyOpsInFlight = Math.max(0, qtyOpsInFlight - 1);
            try {
              liveSelf.disableLoading(liveLine);
            } catch (_) {
              /* ignore */
            }
            try {
              if (marker && window.CartPerformance && window.CartPerformance.measureFromMarker) {
                CartPerformance.measureFromMarker(eventTarget + ':user-action', marker);
              }
            } catch (_) {
              /* ignore */
            }
            if (qtyOpsInFlight === 0) {
              try {
                await ensurePfandMatched(4);
              } catch (e) {
                console.warn('[kc-pfand] post-ensure', e);
                setCheckoutReady(false, waitLabel());
              }
            } else {
              setCheckoutReady(false, waitLabel());
            }
          }
        })();
      };

      // Kette nie abbrechen (sonst bleiben Android/iOS hängen)
      updateChain = updateChain.then(run, run).catch(function (err) {
        console.warn('[kc-pfand] chain', err);
      });
      return updateChain;
    };
  }

  function paintParsedState(cartItemsEl, parsedState, line, quantity, name) {
    const quantityElement =
      document.getElementById('Quantity-' + line) ||
      document.getElementById('Drawer-quantity-' + line);
    const items = document.querySelectorAll('.cart-item');

    try {
      cartItemsEl.classList.toggle('is-empty', parsedState.item_count === 0);
    } catch (_) {
      /* ignore */
    }
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) {
      try {
        cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
      } catch (_) {
        /* ignore */
      }
    }
    if (cartDrawerWrapper) {
      try {
        cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
      } catch (_) {
        /* ignore */
      }
    }

    const sections = (cartItemsEl.getSectionsToRender && cartItemsEl.getSectionsToRender()) || [];
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      const host = document.getElementById(section.id);
      if (!host || !parsedState.sections || !parsedState.sections[section.section]) continue;
      const elementToReplace = host.querySelector(section.selector) || host;
      try {
        elementToReplace.innerHTML = cartItemsEl.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      } catch (_) {
        /* ignore */
      }
    }

    if (quantityElement) {
      const updatedValue =
        parsedState.items && parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
      let message = '';
      if (
        items.length === (parsedState.items && parsedState.items.length) &&
        updatedValue !== parseInt(quantityElement.value, 10)
      ) {
        if (typeof updatedValue === 'undefined') {
          message = (window.cartStrings && window.cartStrings.error) || '';
        } else {
          message = (
            (window.cartStrings && window.cartStrings.quantityError) ||
            ''
          ).replace('[quantity]', updatedValue);
        }
      }
      if (cartItemsEl.updateLiveRegions) cartItemsEl.updateLiveRegions(line, message);
    }

    // Fokus auf Touch-Geräten oft problematisch → nur Desktop
    const coarse =
      window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    if (!coarse && name) {
      const lineItem =
        document.getElementById('CartItem-' + line) ||
        document.getElementById('CartDrawer-Item-' + line);
      if (lineItem) {
        const focusEl = lineItem.querySelector('[name="' + name + '"]');
        if (focusEl) {
          try {
            if (cartDrawerWrapper && typeof trapFocus === 'function') {
              trapFocus(cartDrawerWrapper, focusEl);
            } else {
              focusEl.focus();
            }
          } catch (_) {
            /* ignore */
          }
        }
      }
    }

    reapplyCheckoutVisibility();
  }

  async function sync() {
    if (syncing) {
      pending = true;
      return;
    }
    syncing = true;
    setCheckoutReady(false, waitLabel());
    try {
      await ensurePfandMatched(3);
    } catch (err) {
      console.warn('[kc-pfand]', err);
      setCheckoutReady(false, waitLabel());
      armStuckWatchdog();
    } finally {
      syncing = false;
      if (pending) {
        pending = false;
        scheduleSync(80);
      }
    }
  }

  function scheduleSync(delay) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      sync();
    }, typeof delay === 'number' ? delay : 200);
  }

  function onCartUpdate(event) {
    if (event && event.source === 'kc-pfand') return;
    scheduleSync(280);
  }

  function eventElement(event) {
    let t = event && event.target;
    if (!t) return null;
    if (t.nodeType === 3) t = t.parentElement;
    return t && t.nodeType === 1 ? t : null;
  }

  function submitCartForm(form) {
    if (!form) return;
    try {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'checkout';
      input.value = '1';
      form.appendChild(input);
      if (typeof form.requestSubmit === 'function') {
        // iOS bevorzugt requestSubmit wenn verfügbar — hier ohne Button, daher submit
        form.submit();
      } else {
        form.submit();
      }
    } catch (err) {
      console.warn('[kc-pfand] submit', err);
      try {
        window.location.href = rootPath().replace(/\/?$/, '/') + 'checkout';
      } catch (_) {
        /* ignore */
      }
    }
  }

  async function gateCheckout(event) {
    const target = eventElement(event);
    if (!target || !target.closest) return;

    const isCheckoutBtn = target.closest(CHECKOUT_SEL);
    const isExpress = target.closest(EXPRESS_SEL);
    if (!isCheckoutBtn && !isExpress) return;

    if (matched && !syncing && !checkoutBusy && qtyOpsInFlight === 0) return;

    if (event.cancelable) event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    if (checkoutBusy) return;
    checkoutBusy = true;
    setCheckoutReady(false, waitLabel());

    try {
      await updateChain;
      const ok = await ensurePfandMatched(4);
      if (!ok) {
        setStatus(waitLabel());
        return;
      }

      setCheckoutReady(true);
      checkoutBusy = false;

      if (isCheckoutBtn) {
        const btn = isCheckoutBtn;
        const formId = btn.getAttribute('form');
        const form =
          (formId && document.getElementById(formId)) ||
          btn.closest('form') ||
          document.getElementById('cart') ||
          document.getElementById('CartDrawer-Form');
        submitCartForm(form);
      }
    } catch (err) {
      console.warn('[kc-pfand] checkout gate', err);
      setCheckoutReady(false, waitLabel());
      armStuckWatchdog();
    } finally {
      checkoutBusy = false;
    }
  }

  function installCheckoutGate() {
    if (checkoutGateInstalled) return;
    checkoutGateInstalled = true;

    // click reicht auf iOS/Android; capture damit wir vor Dawn greifen
    document.addEventListener('click', gateCheckout, true);

    document.addEventListener(
      'submit',
      function (event) {
        const form = event.target;
        if (!form || form.tagName !== 'FORM') return;
        if (form.id !== 'cart' && form.id !== 'CartDrawer-Form') return;
        if (matched && !syncing && !checkoutBusy && qtyOpsInFlight === 0) return;

        if (event.cancelable) event.preventDefault();
        event.stopPropagation();
        if (checkoutBusy) return;

        checkoutBusy = true;
        setCheckoutReady(false, waitLabel());

        Promise.resolve()
          .then(function () {
            return updateChain;
          })
          .then(function () {
            return ensurePfandMatched(4);
          })
          .then(function (ok) {
            if (!ok) return;
            setCheckoutReady(true);
            submitCartForm(form);
          })
          .catch(function (err) {
            console.warn('[kc-pfand] submit gate', err);
            setCheckoutReady(false, waitLabel());
            armStuckWatchdog();
          })
          .then(function () {
            checkoutBusy = false;
          });
      },
      true
    );
  }

  function onPageShow(event) {
    // iOS BFCache: Warenkorb kann veraltet sein
    if (event && event.persisted) {
      matched = false;
      setCheckoutReady(false, waitLabel());
      scheduleSync(120);
    }
  }

  function onVisibility() {
    if (document.visibilityState === 'visible' && !matched) {
      scheduleSync(200);
    }
  }

  function boot() {
    setCheckoutReady(false, waitLabel());
    patchCartItems();
    installCheckoutGate();
    scheduleSync(100);

    if (typeof customElements !== 'undefined' && customElements.whenDefined) {
      customElements.whenDefined('cart-items').then(function () {
        patchCartItems();
      }).catch(function () {
        /* ignore */
      });
    }
  }

  setCheckoutReady(false, waitLabel());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Sofort versuchen (defer-Script: DOM oft schon da)
  patchCartItems();
  installCheckoutGate();

  window.addEventListener('pageshow', onPageShow);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('online', function () {
    scheduleSync(100);
  });

  function subscribeCartUpdate() {
    try {
      if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
        return;
      }
    } catch (_) {
      /* ignore */
    }
    setTimeout(subscribeCartUpdate, 250);
  }
  subscribeCartUpdate();

  window.KcPfand = {
    ensure: function () {
      return ensurePfandMatched(4);
    },
    isReady: function () {
      return matched;
    },
  };
})();
